import { Stack, useToast } from '@chakra-ui/react'
import { useState, useEffect } from 'react'
import MonthlyReportHeader from '../../../components/MonthlyReportHeader'
import useNavigateNext from '../../../hooks/useNavigateNext'
import { useCurrentOrPreviousReportsArray } from '../../../hooks/useReports'
import useUpdateReportDoc from '../../../hooks/useUpdateReportDoc'
import FamilySupport from './components/FamilySupport'
import MedicalSupport from './components/MedicalSupport'
import usePurpleBubblesText from '../../../hooks/usePurpleBubblesText'
import PurpleBubbleContent from '../../../components/PurpleBubbleContent'
import MonthlyReportNavButtons from '../../../components/MonthlyReportNavButtons'
import useReportMonthGuard from '../../../hooks/useReportMonthGuard'

export default function SupportPage() {
  const isValidated = useReportMonthGuard()
  const navNext = useNavigateNext()
  const updateReport = useUpdateReportDoc()
  const currentOrPastReports = useCurrentOrPreviousReportsArray()
  const purpleBubbleContent = usePurpleBubblesText('Step-13')
  const toast = useToast()

  const [emotionalAccurate, setEmotionalAccurate] = useState(false)
  const [medicalAccurate, setMedicalAccurate] = useState(false)

  useEffect(() => {
    const report = currentOrPastReports[0]
    if (report?.emotionalSupportListIsAccurate !== undefined) {
      setEmotionalAccurate(report.emotionalSupportListIsAccurate ?? false)
    }
    if (report?.medicalSupportListIsAccurate !== undefined) {
      setMedicalAccurate(report.medicalSupportListIsAccurate ?? false)
    }
  }, [currentOrPastReports])

  if (!isValidated) {
    return null // Don't render anything until month is validated
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()

        // Check if the current report exists before accessing its properties
        if (currentOrPastReports[0]) {
          // Require the emotional & physical support accuracy checkbox
          if (!emotionalAccurate) {
            toast({
              status: 'warning',
              title: 'Confirmation Required',
              description:
                'Please confirm the Emotional & Physical Support information is accurate before continuing.',
            })
            return false
          }

          // Require the medical support accuracy checkbox
          if (!medicalAccurate) {
            toast({
              status: 'warning',
              title: 'Confirmation Required',
              description:
                'Please confirm the Medical Professional information is accurate before continuing.',
            })
            return false
          }

          // Require the ALS clinic question to be answered
          const alsClinic = currentOrPastReports[0].alsClinic as
            | { attended: boolean | null; rating: number | null }
            | undefined
          if (alsClinic?.attended === null || alsClinic?.attended === undefined) {
            toast({
              status: 'warning',
              title: 'Response Required',
              description:
                'Please indicate whether you attended an ALS clinic this month.',
            })
            return false
          }

          // If attended, require a rating
          if (alsClinic.attended === true && !alsClinic.rating) {
            toast({
              status: 'warning',
              title: 'Rating Required',
              description:
                'Please rate your clinic experience before continuing.',
            })
            return false
          }
        }

        navNext()

        // Safely access properties with optional chaining and nullish coalescing
        const emotionalSupportPeople =
          currentOrPastReports[0]?.emotionalSupportPeople ||
          currentOrPastReports[1]?.emotionalSupportPeople ||
          []

        const medicalSupport =
          currentOrPastReports[0]?.medicalSupport ||
          currentOrPastReports[1]?.medicalSupport ||
          {}

        updateReport({
          support: true,
          emotionalSupportPeople,
          medicalSupport,
        })
      }}
    >
      <Stack spacing={5}>
        <MonthlyReportHeader title='Tell us about your Support Team.' />
        {purpleBubbleContent?.text && (
          <PurpleBubbleContent text={purpleBubbleContent.text} />
        )}
        <FamilySupport onAccurateChange={setEmotionalAccurate} />
        <MedicalSupport onAccurateChange={setMedicalAccurate} />
        <MonthlyReportNavButtons />
      </Stack>
    </form>
  )
}
